import { type FormEvent, useEffect, useState } from "react";
import { api } from "./api";
import { useTranslation } from "./i18n";

type Props = {
  restaurantId: string;
  onComplete: () => void;
};

type ConnectedIntegration = {
  id: string;
  platform: string;
  restaurant_id: string;
  is_sandbox: boolean;
  status: string;
};

const PLATFORMS = [
  { key: "wolt", icon: "\u{1F7E6}", label: "Wolt" },
  { key: "uber_eats", icon: "\u{1F7E9}", label: "Uber Eats" },
  { key: "glovo", icon: "\u{1F7E8}", label: "Glovo" },
  { key: "pyszne", icon: "\u{1F7E7}", label: "Pyszne.pl" },
  { key: "bolt_food", icon: "\u{1F7EA}", label: "Bolt Food" },
] as const;

const PLATFORM_HELP: Record<
  string,
  { idLabel: string; secretLabel: string; hint: string; secretField: "webhook_secret" | "api_key" }
> = {
  wolt: {
    idLabel: "Venue ID",
    secretLabel: "Webhook Secret (HMAC)",
    hint: "Find these in the Wolt Partner Portal under Settings > API.",
    secretField: "webhook_secret",
  },
  uber_eats: {
    idLabel: "Store ID",
    secretLabel: "Client Secret",
    hint: "Find these in the Uber Eats restaurant dashboard under Integrations.",
    secretField: "webhook_secret",
  },
  glovo: {
    idLabel: "Store ID",
    secretLabel: "Webhook Secret",
    hint: "Find these in the Glovo Partner Portal under API Settings.",
    secretField: "webhook_secret",
  },
  pyszne: {
    idLabel: "Restaurant ID",
    secretLabel: "X-API-Key",
    hint: "Find these in the Pyszne.pl (JET Connect) partner dashboard.",
    secretField: "api_key",
  },
  bolt_food: {
    idLabel: "Provider ID",
    secretLabel: "Webhook Secret",
    hint: "Find these in the Bolt Food Partner Portal under Settings.",
    secretField: "webhook_secret",
  },
};

type WizardStep = "welcome" | "connect" | "test" | "live" | "complete";
const STEPS: WizardStep[] = ["welcome", "connect", "test", "live", "complete"];

type Readiness = {
  ready_for_live_test: boolean;
  live_webhook_verified: boolean;
  checks: Record<string, boolean>;
  issues: Record<string, string>;
};

export function OnboardingWizard({ restaurantId, onComplete }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>("welcome");

  // Connect step state
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [extId, setExtId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [chainId, setChainId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectedIntegration, setConnectedIntegration] = useState<ConnectedIntegration | null>(null);

  // Test step state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"idle" | "success" | "error">("idle");
  const [testError, setTestError] = useState<string | null>(null);

  // Go Live step state
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [liveSecret, setLiveSecret] = useState("");
  const [goingLive, setGoingLive] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);

  const stepIndex = STEPS.indexOf(step);

  // Resume an interrupted onboarding: a venue that is already connected must
  // not be offered for a second Connect, which would fail on the venue
  // uniqueness constraint or consume another plan slot.
  useEffect(() => {
    let cancelled = false;
    api
      .listIntegrations(restaurantId)
      .then((items) => {
        if (cancelled) return;
        const existing = items.find((item) => item.status === "active");
        if (!existing) return;
        setConnectedIntegration(existing);
        setSelectedPlatform(existing.platform);
        setExtId(existing.external_restaurant_id);
        setStep(existing.is_sandbox ? "test" : "complete");
      })
      .catch(() => {
        /* start from the beginning when the list cannot be read */
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  useEffect(() => {
    if (step !== "live" || !connectedIntegration) return;
    let cancelled = false;
    setReadiness(null);
    api
      .getIntegrationReadiness(connectedIntegration.id)
      .then((r) => {
        if (!cancelled) setReadiness(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLiveError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [step, connectedIntegration]);

  const handleConnect = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPlatform) return;
    setSubmitting(true);
    setConnectError(null);
    try {
      const payload: Record<string, unknown> = {
        restaurant_id: restaurantId,
        platform: selectedPlatform,
        external_restaurant_id: extId,
        is_sandbox: true,
        status: "active",
      };
      const secretField = platformHelp?.secretField ?? "api_key";
      if (apiKey.trim()) payload[secretField] = apiKey.trim();
      if (chainId.trim()) payload.config = { chain_id: chainId.trim() };
      const integration = await api.createIntegration(payload);
      setConnectedIntegration(integration);
      setStep("test");
    } catch (err: unknown) {
      setConnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async () => {
    if (!connectedIntegration) return;
    setTesting(true);
    setTestResult("idle");
    setTestError(null);
    try {
      await api.triggerSimulatedOrder(
        connectedIntegration.id,
        connectedIntegration.platform,
      );
      setTestResult("success");
    } catch (err: unknown) {
      setTestResult("error");
      setTestError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  };

  const handleGoLive = async () => {
    if (!connectedIntegration) return;
    setGoingLive(true);
    setLiveError(null);
    try {
      const payload: Record<string, unknown> = { is_sandbox: false };
      if (liveSecret.trim()) payload.webhook_secret = liveSecret.trim();
      const updated = await api.updateIntegration(connectedIntegration.id, payload);
      setConnectedIntegration({ ...connectedIntegration, ...updated });
      setStep("complete");
    } catch (err: unknown) {
      setLiveError(err instanceof Error ? err.message : String(err));
    } finally {
      setGoingLive(false);
    }
  };

  const handleFinish = async () => {
    onComplete();
  };

  const refreshReadiness = async () => {
    if (!connectedIntegration) return;
    setReadiness(null);
    setLiveError(null);
    try {
      setReadiness(await api.getIntegrationReadiness(connectedIntegration.id));
    } catch (err: unknown) {
      setLiveError(err instanceof Error ? err.message : String(err));
    }
  };

  const platformHelp = selectedPlatform ? PLATFORM_HELP[selectedPlatform] : null;
  const webhookUrl = selectedPlatform
    ? `${window.location.origin}/api/webhooks/${selectedPlatform}`
    : "";

  return (
    <div className="onboarding-wizard">
      {/* Progress bar */}
      <div className="wizard-progress">
        {STEPS.map((s, i) => (
          <div key={s} className="wizard-progress-step">
            <div
              className={`wizard-progress-dot ${i < stepIndex ? "done" : ""} ${i === stepIndex ? "active" : ""}`}
            >
              {i < stepIndex ? "\u2713" : i + 1}
            </div>
            <span className={`wizard-progress-label ${i === stepIndex ? "active" : ""}`}>
              {t(`onboarding.step_${s}`)}
            </span>
            {i < STEPS.length - 1 && (
              <div className={`wizard-progress-line ${i < stepIndex ? "done" : ""}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="wizard-content">
        {step === "welcome" && (
          <div className="wizard-step-welcome">
            <div className="wizard-hero-icon">&#x1F680;</div>
            <h2>{t("onboarding.welcome_title")}</h2>
            <p className="muted">{t("onboarding.welcome_desc")}</p>
            <div className="wizard-checklist">
              <div className="wizard-checklist-item">
                <span className="wizard-check-icon">&#x1F517;</span>
                <div>
                  <strong>{t("onboarding.checklist_connect")}</strong>
                  <p className="muted">{t("onboarding.checklist_connect_desc")}</p>
                </div>
              </div>
              <div className="wizard-checklist-item">
                <span className="wizard-check-icon">&#x1F9EA;</span>
                <div>
                  <strong>{t("onboarding.checklist_test")}</strong>
                  <p className="muted">{t("onboarding.checklist_test_desc")}</p>
                </div>
              </div>
              <div className="wizard-checklist-item">
                <span className="wizard-check-icon">&#x1F389;</span>
                <div>
                  <strong>{t("onboarding.checklist_live")}</strong>
                  <p className="muted">{t("onboarding.checklist_live_desc")}</p>
                </div>
              </div>
            </div>
            <button
              className="btn btn-primary wizard-btn-lg"
              onClick={() => setStep("connect")}
            >
              {t("onboarding.get_started")}
            </button>
          </div>
        )}

        {step === "connect" && (
          <div className="wizard-step-connect">
            <h2>{t("onboarding.connect_title")}</h2>
            <p className="muted">{t("onboarding.connect_desc")}</p>

            {!selectedPlatform ? (
              <div className="platform-grid wizard-platform-grid">
                {PLATFORMS.map((p) => (
                  <div
                    key={p.key}
                    className="platform-card"
                    onClick={() => setSelectedPlatform(p.key)}
                  >
                    <span className="platform-icon">{p.icon}</span>
                    <div style={{ fontWeight: 600 }}>{p.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <form onSubmit={handleConnect} className="wizard-connect-form">
                <div className="wizard-platform-selected">
                  <span className="platform-icon">
                    {PLATFORMS.find((p) => p.key === selectedPlatform)?.icon}
                  </span>
                  <strong>
                    {PLATFORMS.find((p) => p.key === selectedPlatform)?.label}
                  </strong>
                  <button
                    type="button"
                    className="btn btn-secondary-sm"
                    onClick={() => {
                      setSelectedPlatform(null);
                      setExtId("");
                      setApiKey("");
                      setChainId("");
                      setConnectError(null);
                    }}
                  >
                    {t("onboarding.change_platform")}
                  </button>
                </div>

                {platformHelp && (
                  <div className="wizard-hint-box">
                    <span>&#x1F4A1;</span> {platformHelp.hint}
                  </div>
                )}

                <label className="label">
                  {platformHelp?.idLabel ?? t("integrations.ext_id")}
                </label>
                <input
                  className="input"
                  placeholder={t("onboarding.ext_id_placeholder")}
                  value={extId}
                  onChange={(e) => setExtId(e.target.value)}
                  required
                  style={{ marginBottom: 16 }}
                />

                <label className="label">
                  {platformHelp?.secretLabel ?? t("integrations.api_key")}{" "}
                  <span className="muted" style={{ fontSize: 12 }}>({t("onboarding.optional_for_sandbox")})</span>
                </label>
                <input
                  className="input"
                  type="password"
                  placeholder={t("onboarding.secret_placeholder")}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  style={{ marginBottom: 16 }}
                />

                {selectedPlatform === "glovo" && (
                  <>
                    <label className="label">{t("onboarding.chain_id_label")}</label>
                    <input
                      className="input"
                      placeholder={t("onboarding.chain_id_placeholder")}
                      value={chainId}
                      onChange={(e) => setChainId(e.target.value)}
                      style={{ marginBottom: 4 }}
                    />
                    <p className="muted" style={{ fontSize: 12, marginBottom: 16 }}>
                      {t("onboarding.chain_id_hint")}
                    </p>
                  </>
                )}

                <div className="wizard-webhook-url">
                  <label className="label">{t("onboarding.webhook_url_label")}</label>
                  <div className="wizard-webhook-value">
                    <code>{webhookUrl}</code>
                    <button
                      type="button"
                      className="btn btn-secondary-sm"
                      onClick={() => navigator.clipboard.writeText(webhookUrl)}
                    >
                      {t("onboarding.copy")}
                    </button>
                  </div>
                  <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {t("onboarding.webhook_url_hint")}
                  </p>
                </div>

                {connectError && (
                  <p className="error" style={{ marginTop: 12 }}>
                    {connectError}
                  </p>
                )}

                <div className="wizard-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStep("welcome")}
                  >
                    {t("integrations.back")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? t("integrations.connecting")
                      : t("onboarding.connect_and_test")}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {step === "test" && (
          <div className="wizard-step-test">
            <h2>{t("onboarding.test_title")}</h2>
            <p className="muted">{t("onboarding.test_desc")}</p>

            <div className="wizard-test-card">
              {connectedIntegration && (
                <div className="wizard-test-platform">
                  <span className="platform-icon">
                    {PLATFORMS.find((p) => p.key === connectedIntegration.platform)?.icon}
                  </span>
                  <div>
                    <strong>
                      {PLATFORMS.find((p) => p.key === connectedIntegration.platform)?.label}
                    </strong>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                      {t("onboarding.sandbox_active")}
                    </span>
                  </div>
                </div>
              )}

              {testResult === "idle" && (
                <button
                  className="btn btn-primary wizard-btn-lg"
                  onClick={handleTest}
                  disabled={testing}
                >
                  {testing
                    ? t("onboarding.sending_test")
                    : t("onboarding.send_test_order")}
                </button>
              )}

              {testResult === "success" && (
                <div className="wizard-test-success">
                  <div className="wizard-success-icon">&#x2705;</div>
                  <h3>{t("onboarding.test_success")}</h3>
                  <p className="muted">{t("onboarding.test_success_desc")}</p>
                </div>
              )}

              {testResult === "error" && (
                <div className="wizard-test-error">
                  <p className="error">{testError}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setTestResult("idle");
                      setTestError(null);
                    }}
                  >
                    {t("onboarding.retry")}
                  </button>
                </div>
              )}
            </div>

            <div className="wizard-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep("connect")}
              >
                {t("integrations.back")}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setStep("live")}
                disabled={testResult !== "success"}
              >
                {t("integrations.continue")}
              </button>
            </div>
          </div>
        )}

        {step === "live" && (
          <div className="wizard-step-live">
            <h2>{t("onboarding.go_live_title")}</h2>
            <p className="muted">{t("onboarding.go_live_desc")}</p>

            {connectedIntegration && (
              <div className="wizard-test-card">
                <div className="wizard-test-platform">
                  <span className="platform-icon">
                    {PLATFORMS.find((p) => p.key === connectedIntegration.platform)?.icon}
                  </span>
                  <div>
                    <strong>
                      {PLATFORMS.find((p) => p.key === connectedIntegration.platform)?.label}
                    </strong>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                      {t("onboarding.sandbox_active")}
                    </span>
                  </div>
                </div>

                <label className="label" style={{ marginTop: 12 }}>
                  {t("onboarding.live_secret_label")}
                </label>
                <input
                  className="input"
                  type="password"
                  placeholder={t("onboarding.live_secret_placeholder")}
                  value={liveSecret}
                  onChange={(e) => setLiveSecret(e.target.value)}
                  style={{ marginBottom: 4 }}
                />
                <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
                  {t("onboarding.live_secret_hint")}
                </p>

                {readiness && (
                  <div className="wizard-readiness">
                    <strong>{t("onboarding.live_readiness")}</strong>
                    {Object.entries(readiness.checks).map(([key, ok]) => (
                      <div key={key} className="wizard-readiness-row">
                        <span>{ok ? "\u2705" : "\u274C"}</span>
                        <span>{t(`onboarding.check_${key}`)}</span>
                      </div>
                    ))}
                    {!readiness.ready_for_live_test && (
                      <p className="error" style={{ marginTop: 8 }}>
                        {t("onboarding.live_not_ready")}
                      </p>
                    )}
                  </div>
                )}

                {readiness && !readiness.checks.oauth && connectedIntegration.platform === "wolt" && (
                  <div className="wizard-hint-box" style={{ marginTop: 12 }}>
                    <span>&#x231B;</span> {t("onboarding.live_oauth_pending")}
                  </div>
                )}

                {liveError && (
                  <p className="error" style={{ marginTop: 12 }}>
                    {liveError}
                  </p>
                )}
              </div>
            )}

            <div className="wizard-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setStep("test")}
              >
                {t("integrations.back")}
              </button>
              {readiness && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={refreshReadiness}
                >
                  {t("onboarding.refresh_readiness")}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGoLive}
                disabled={
                  goingLive ||
                  !readiness ||
                  (!readiness.checks.webhook_secret && !liveSecret.trim()) ||
                  (connectedIntegration?.platform === "wolt" && !readiness.checks.oauth)
                }
              >
                {goingLive ? t("onboarding.going_live") : t("onboarding.go_live")}
              </button>
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="wizard-step-complete">
            <div className="wizard-hero-icon">&#x1F389;</div>
            <h2>{t("onboarding.complete_title")}</h2>
            <p className="muted">{t("onboarding.complete_desc")}</p>

            <div className="wizard-summary">
              <div className="wizard-summary-row">
                <span className="wizard-summary-icon done">&#x2713;</span>
                <span>{t("onboarding.summary_restaurant")}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-icon done">&#x2713;</span>
                <span>
                  {t("onboarding.summary_integration", {
                    platform:
                      PLATFORMS.find((p) => p.key === connectedIntegration?.platform)
                        ?.label ?? "",
                  })}
                </span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-icon done">&#x2713;</span>
                <span>{t("onboarding.summary_test")}</span>
              </div>
            </div>

            <div className="wizard-next-steps">
              <h3>{t("onboarding.next_steps")}</h3>
              <ul>
                <li>{t("onboarding.next_webhook")}</li>
                <li>{t("onboarding.next_more_platforms")}</li>
                <li>{t("onboarding.next_printer")}</li>
                <li>{t("onboarding.next_menu")}</li>
              </ul>
            </div>

            <button
              className="btn btn-primary wizard-btn-lg"
              onClick={handleFinish}
            >
              {t("onboarding.go_to_dashboard")}
            </button>
          </div>
        )}
      </div>

      {/* Skip link */}
      {step !== "complete" && (
        <div className="wizard-skip">
          <button className="btn-link" onClick={onComplete}>
            {t("onboarding.skip")}
          </button>
        </div>
      )}
    </div>
  );
}
